import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Edit2, Search, Users } from 'lucide-react';
import { primaryButton, ghostButton, inputStyle, glass, glassLight, T, Badge, StatCard, EmptyState, Field } from '../ui';
interface Cliente {
  id: string;
  codigo: string;
  nombre: string;
  rnc: string;
  contacto: string;
  telefono: string;
  email: string;
  direccion: string;
  ubicacion: string;
  tipo: 'Industria' | 'Comercio' | 'Residencial' | 'Otro';
  capacidadTanque: number;
  estado: 'activo' | 'inactivo';
  observaciones: string;
  fecha_registro: string;
  ultima_compra?: string;
  dias_sin_comprar?: number;
  representante?: string;
  representanteCedula?: string;
  representanteDireccion?: string;
}
interface ClientesProps {
  clientes: Cliente[];
  onAddCliente: (cliente: Cliente) => void;
  onDeleteCliente: (id: string) => void;
  onUpdateCliente: (cliente: Cliente) => void;
}
const iconBtn: React.CSSProperties = {
  width: 30,
  height: 30,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 8,
  border: `1px solid ${T.border}`,
  background: T.surface,
  cursor: 'pointer'
};
export function Clientes({
  clientes,
  onAddCliente,
  onDeleteCliente,
  onUpdateCliente
}: ClientesProps) {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState<'todos' | 'activo' | 'inactivo'>('todos');
  const [formData, setFormData] = useState<Partial<Cliente>>({
    tipo: 'Comercio',
    estado: 'activo',
    capacidadTanque: 1000
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.codigo || !formData.nombre || !formData.email) {
      alert('Por favor completa los campos obligatorios');
      return;
    }
    if (editingId) {
      onUpdateCliente({
        ...formData,
        id: editingId
      } as Cliente);
      setEditingId(null);
    } else {
      onAddCliente({
        ...formData,
        id: Date.now().toString(),
        fecha_registro: new Date().toISOString().split('T')[0]
      } as Cliente);
    }
    setFormData({
      tipo: 'Comercio',
      estado: 'activo',
      capacidadTanque: 1000
    });
    setShowForm(false);
  };
  const handleEdit = (cliente: Cliente) => {
    setFormData(cliente);
    setEditingId(cliente.id);
    setShowForm(true);
  };
  const filteredClientes = clientes.filter(c => {
    const matchSearch = c.codigo.toLowerCase().includes(searchTerm.toLowerCase()) || c.nombre.toLowerCase().includes(searchTerm.toLowerCase()) || c.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchEstado = filterEstado === 'todos' || c.estado === filterEstado;
    return matchSearch && matchEstado;
  });
  const clientesActivos = clientes.filter(c => c.estado === 'activo').length;
  const clientesInactivos = clientes.filter(c => c.estado === 'inactivo').length;
  return <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: 20
  }} data-id="element-154" data-mp="2cphgn7jwkr2n">
      {/* Header Stats */}
      <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      gap: 14
    }} data-id="element-155" data-mp="2gq1cm7sqpon7">
        
        <StatCard label="Total clientes" value={String(clientes.length)} note="Registrados en el sistema" accent={T.info} data-id="element-156" data-mp="27qwg170qo5do" />
        
        <StatCard label="Activos" value={String(clientesActivos)} note="Comprando actualmente" accent={T.ok} data-id="element-157" data-mp="2vhsbce4elipo" />
        
        <StatCard label="Inactivos" value={String(clientesInactivos)} note="Sin actividad reciente" accent={T.danger} data-id="element-158" data-mp="30ig69avr6jp6" />
        
      </div>

      {/* Controles */}
      <div style={{
      ...glassLight,
      padding: 16
    }} data-id="element-159" data-mp="2qp903nt6uzo1">
        <div style={{
        display: 'flex',
        gap: 10,
        flexWrap: 'wrap',
        alignItems: 'center'
      }} data-id="element-160" data-mp="3efmi0li27i4w">
          
          <div style={{
          flex: '1 1 240px',
          position: 'relative'
        }} data-id="element-161" data-mp="3j8fnjblcqui1">
            <Search size={16} style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: T.textMuted
          }} data-id="element-162" data-mp="1umlc5sijguh7" />
            
            <input type="text" placeholder="Buscar por código, nombre o email..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{
            ...inputStyle,
            paddingLeft: 36
          }} data-id="element-163" data-mp="1lmg7vr8n6c79" />
            
          </div>
          <select value={filterEstado} onChange={e => setFilterEstado(e.target.value as any)} style={{
          ...inputStyle,
          width: 'auto'
        }} data-id="element-164" data-mp="29aze8184sewp">
            
            <option value="todos" data-id="element-165" data-mp="1zkmw0fnsgxtq">Todos los estados</option>
            <option value="activo" data-id="element-166" data-mp="1zi0pmjcmwod3">Activos</option>
            <option value="inactivo" data-id="element-167" data-mp="2wzsda6k1v2vj">Inactivos</option>
          </select>
          <button onClick={() => {
          setShowForm(!showForm);
          setEditingId(null);
          setFormData({
            tipo: 'Comercio',
            estado: 'activo',
            capacidadTanque: 1000
          });
        }} style={{
          ...primaryButton,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6
        }} data-id="element-168" data-mp="2s8sip7yekww7">
            
            <Plus size={15} data-id="element-169" data-mp="39kh8hu2k5c41" /> Nuevo Cliente
          </button>
        </div>
      </div>

      {/* Formulario */}
      <AnimatePresence data-id="element-170" data-mp="3iiszvyb90dxj">
        {showForm && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.2
      }} style={{
        overflow: 'hidden'
      }} data-id="element-171" data-mp="3ihrhf8rwkp5s">
          
            <div style={{
          ...glass,
          padding: 20
        }} data-id="element-172" data-mp="10zxacr1p86o">
              <h3 style={{
            fontSize: 15,
            fontWeight: 800,
            marginBottom: 16,
            color: T.text
          }} data-id="element-173" data-mp="62768f4xcdex">
              
                {editingId ? 'Editar Cliente' : 'Nuevo Cliente'}
              </h3>
              <form onSubmit={handleSubmit} data-id="element-174" data-mp="271j5jt3zqf9j">
                <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 14
            }} data-id="element-175" data-mp="225bvbo4kahls">
                
                  <Field label="Código *" data-id="element-176" data-mp="2fxs5d8vvrfw7">
                    <input type="text" value={formData.codigo || ''} onChange={e => setFormData({
                  ...formData,
                  codigo: e.target.value
                })} style={inputStyle} placeholder="CLI-001" data-id="element-177" data-mp="2b460i2vykgay" />
                  
                  </Field>
                  <Field label="Nombre de empresa *" data-id="element-178" data-mp="2yujlxak1fgwk">
                    <input type="text" value={formData.nombre || ''} onChange={e => setFormData({
                  ...formData,
                  nombre: e.target.value
                })} style={inputStyle} placeholder="Nombre empresa" data-id="element-179" data-mp="2yu0amcow7qri" />
                  
                  </Field>
                  <Field label="RNC" data-id="element-180" data-mp="10gi4csucvwjm">
                    <input type="text" value={formData.rnc || ''} onChange={e => setFormData({
                  ...formData,
                  rnc: e.target.value
                })} style={inputStyle} placeholder="123456789" data-id="element-181" data-mp="10hjg6zj74n2e" />
                  
                  </Field>
                  <Field label="Tipo de cliente" data-id="element-182" data-mp="gl6u27th89ja">
                    <select value={formData.tipo || 'Comercio'} onChange={e => setFormData({
                  ...formData,
                  tipo: e.target.value as any
                })} style={inputStyle} data-id="element-183" data-mp="lk06ry0xvwie">
                    
                      <option data-id="element-184" data-mp="bn5ehwv9esck">Industria</option>
                      <option data-id="element-185" data-mp="3tryvjzjqr79x">Comercio</option>
                      <option data-id="element-186" data-mp="3tta6qkfinhb2">Residencial</option>
                      <option data-id="element-187" data-mp="2lo0ql5br2r1">Otro</option>
                    </select>
                  </Field>
                  <Field label="Persona de contacto" data-id="element-188" data-mp="1s0m1k5jbajsn">
                    <input type="text" value={formData.contacto || ''} onChange={e => setFormData({
                  ...formData,
                  contacto: e.target.value
                })} style={inputStyle} placeholder="Nombre contacto" data-id="element-189" data-mp="1s1x1tvdjt9f0" />
                  
                  </Field>
                  <Field label="Propietario / representante legal" data-id="element-190" data-mp="30m2qntwx8msx">
                    <input type="text" value={formData.representante || ''} onChange={e => setFormData({
                  ...formData,
                  representante: e.target.value
                })} style={inputStyle} placeholder="Nombre completo (para el contrato)" data-id="element-191" data-mp="2vu2qc0cxvrvt" />
                  
                  </Field>
                  <Field label="Cédula del representante" data-id="element-192" data-mp="2hua34f0mftsl">
                    <input type="text" value={formData.representanteCedula || ''} onChange={e => setFormData({
                  ...formData,
                  representanteCedula: e.target.value
                })} style={inputStyle} placeholder="000-0000000-0" data-id="element-193" data-mp="2hww9ibbs0398" />
                  
                  </Field>
                  <Field label="Teléfono" data-id="element-194" data-mp="72icwqlow6t1">
                    <input type="tel" value={formData.telefono || ''} onChange={e => setFormData({
                  ...formData,
                  telefono: e.target.value
                })} style={inputStyle} placeholder="+1-809-..." data-id="element-195" data-mp="73thgsmylil7" />
                  
                  </Field>
                  <Field label="Email *" data-id="element-196" data-mp="1ypomhucgecjl">
                    <input type="email" value={formData.email || ''} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} style={inputStyle} placeholder="empresa@email.com" data-id="element-197" data-mp="1ys1aao4w7h4k" />
                  
                  </Field>
                  <Field label="Ubicación" data-id="element-198" data-mp="1b0vsi3fe6erm">
                    <input type="text" value={formData.ubicacion || ''} onChange={e => setFormData({
                  ...formData,
                  ubicacion: e.target.value
                })} style={inputStyle} placeholder="Zona/Región" data-id="element-199" data-mp="1f27apnvxrxps" />
                  
                  </Field>
                  <Field label="Capacidad tanque (galones)" data-id="element-200" data-mp="2vg7a8xqvdlci">
                    <input type="number" value={formData.capacidadTanque || 1000} onChange={e => setFormData({
                  ...formData,
                  capacidadTanque: parseInt(e.target.value)
                })} style={inputStyle} placeholder="1000" data-id="element-201" data-mp="2vemfp84k22en" />
                  
                  </Field>
                  <Field label="Estado" data-id="element-202" data-mp="3l1n738k39a10">
                    <select value={formData.estado || 'activo'} onChange={e => setFormData({
                  ...formData,
                  estado: e.target.value as any
                })} style={inputStyle} data-id="element-203" data-mp="3pzoprsdlr7by">
                    
                      <option value="activo" data-id="element-204" data-mp="2xc2c1ranumbd">Activo</option>
                      <option value="inactivo" data-id="element-205" data-mp="32alzxx8te86d">Inactivo</option>
                    </select>
                  </Field>
                  <div style={{
                gridColumn: '1 / -1'
              }} data-id="element-206" data-mp="36c7ju2jrnmq2">
                    <Field label="Dirección" data-id="element-207" data-mp="hkep8opd2a6a">
                      <input type="text" value={formData.direccion || ''} onChange={e => setFormData({
                    ...formData,
                    direccion: e.target.value
                  })} style={inputStyle} placeholder="Dirección completa" data-id="element-208" data-mp="hjlg5h2xj8kz" />
                    
                    </Field>
                  </div>
                  <div style={{
                gridColumn: '1 / -1'
              }} data-id="element-209" data-mp="224jn3dvjaw3m">
                    <Field label="Domicilio personal del representante (para el contrato)" data-id="element-210" data-mp="1x9cmiexka7ix">
                      <input type="text" value={formData.representanteDireccion || ''} onChange={e => setFormData({
                    ...formData,
                    representanteDireccion: e.target.value
                  })} style={inputStyle} placeholder="Calle, sector, ciudad" data-id="element-211" data-mp="1sa9t5rqy1pqs" />
                    
                    </Field>
                  </div>
                  <div style={{
                gridColumn: '1 / -1'
              }} data-id="element-212" data-mp="19e9ujy3gnny8">
                    <Field label="Observaciones" data-id="element-213" data-mp="38r5lkbivbbv3">
                      <textarea value={formData.observaciones || ''} onChange={e => setFormData({
                    ...formData,
                    observaciones: e.target.value
                  })} style={{
                    ...inputStyle,
                    resize: 'vertical'
                  }} rows={3} placeholder="Notas adicionales..." data-id="element-214" data-mp="38ojhwmz3lgi5" />
                    
                    </Field>
                  </div>
                </div>
                <div style={{
              display: 'flex',
              gap: 8,
              marginTop: 16
            }} data-id="element-215" data-mp="3iu197lxi9m47">
                  <button type="submit" style={primaryButton} data-id="element-216" data-mp="ag2c2gysrup4">
                    {editingId ? 'Actualizar' : 'Guardar'} cliente
                  </button>
                  <button type="button" onClick={() => {
                setShowForm(false);
                setEditingId(null);
              }} style={ghostButton} data-id="element-217" data-mp="gtme0ayt28k">
                  
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Lista de Clientes */}
      <div style={{
      ...glass,
      padding: 20
    }} data-id="element-218" data-mp="2q3ivlqz5o2cj">
        <h3 style={{
        fontSize: 15,
        fontWeight: 800,
        marginBottom: 16,
        color: T.text
      }} data-id="element-219" data-mp="2il1h16cf9xcp">
          
          Clientes ({filteredClientes.length})
        </h3>
        {filteredClientes.length === 0 ? <EmptyState icon={<Users size={20} data-id="element-221" data-mp="2mny4pbgtkjni" />} title="No hay clientes" hint="Registra un cliente nuevo o ajusta los filtros de búsqueda." data-id="element-220" data-mp="2rkyadl9rbke3" /> : <div style={{
        overflowX: 'auto'
      }} data-id="element-222" data-mp="1yxvgj6g0zxu6">
            <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 13
        }} data-id="element-223" data-mp="1ywamdhxmigwu">
              <thead data-id="element-224" data-mp="28nwtp36ubvub">
                <tr data-id="element-225" data-mp="28ogb8iskwi7t">
                  {['Código', 'Empresa', 'Tipo', 'Contacto', 'Teléfono', 'Tanque (gal)', 'Estado', 'Acciones'].map((h, i) => <th key={h} style={{
                textAlign: i === 7 ? 'center' : 'left',
                padding: '0 10px 10px',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: T.textMuted,
                borderBottom: `1px solid ${T.border}`
              }} data-id="element-226" data-mp="23vx15vt3o57r" data-mpi={i}>
                  
                      {h}
                    </th>)}
                </tr>
              </thead>
              <tbody data-id="element-227" data-mp="1yseat7zoc0lt">
                {filteredClientes.map(cliente => <tr key={cliente.id} data-id="element-228" data-mp="23kxpy9e0f3sq">
                    <td style={{
                padding: '12px 10px',
                borderBottom: `1px solid ${T.border}`,
                fontFamily: 'monospace',
                color: T.info,
                fontWeight: 700
              }} data-id="element-229" data-mp="23lz2kiaobyck">
                  
                      {cliente.codigo}
                    </td>
                    <td style={{
                padding: '12px 10px',
                borderBottom: `1px solid ${T.border}`,
                fontWeight: 600,
                color: T.text
              }} data-id="element-230" data-mp="2mj9xxrt6q7mq">
                  
                      {cliente.nombre}
                    </td>
                    <td style={{
                padding: '12px 10px',
                borderBottom: `1px solid ${T.border}`
              }} data-id="element-231" data-mp="s73mcrwdpotw">
                  
                      <span style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '3px 9px',
                  borderRadius: 99,
                  background: T.surfaceSubtle,
                  color: T.textSecondary
                }} data-id="element-232" data-mp="30o0k5yydch5x">
                    
                        {cliente.tipo}
                      </span>
                    </td>
                    <td style={{
                padding: '12px 10px',
                borderBottom: `1px solid ${T.border}`,
                color: T.textSecondary
              }} data-id="element-233" data-mp="3ke470f0zjtmp">
                  
                      {cliente.contacto}
                    </td>
                    <td style={{
                padding: '12px 10px',
                borderBottom: `1px solid ${T.border}`,
                color: T.textSecondary
              }} data-id="element-234" data-mp="23z2ngb726964">
                  
                      {cliente.telefono}
                    </td>
                    <td style={{
                padding: '12px 10px',
                borderBottom: `1px solid ${T.border}`,
                color: T.text
              }} data-id="element-235" data-mp="20esrys29hhkn">
                  
                      {cliente.capacidadTanque}
                    </td>
                    <td style={{
                padding: '12px 10px',
                borderBottom: `1px solid ${T.border}`
              }} data-id="element-236" data-mp="1wdgww70mc452">
                  
                      <Badge label={cliente.estado} color={cliente.estado === 'activo' ? T.ok : T.danger} soft={cliente.estado === 'activo' ? T.okSoft : T.dangerSoft} data-id="element-237" data-mp="1mhdxva12clmc" />
                  
                    </td>
                    <td style={{
                padding: '12px 10px',
                borderBottom: `1px solid ${T.border}`,
                textAlign: 'center'
              }} data-id="element-238" data-mp="1cq9ezz89ogm8">
                  
                      <div style={{
                  display: 'inline-flex',
                  gap: 6,
                  justifyContent: 'center'
                }} data-id="element-239" data-mp="13qminruea101">
                    
                        <button onClick={() => handleEdit(cliente)} title="Editar" style={{
                    ...iconBtn,
                    color: T.info
                  }} data-id="element-240" data-mp="f5zy1bdky759">
                      
                          <Edit2 size={14} data-id="element-241" data-mp="p3e9ewknc5qp" />
                        </button>
                        <button onClick={() => onDeleteCliente(cliente.id)} title="Eliminar" style={{
                    ...iconBtn,
                    color: T.danger
                  }} data-id="element-242" data-mp="1335j4l44qzyw">
                      
                          <Trash2 size={14} data-id="element-243" data-mp="1ctrw07kgpcdw" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>
    </div>;
}